import { useMemo } from 'react';
import { Pressable, View, ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { isToday, isYesterday, format, formatISO } from 'date-fns';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { PlannedDateRange } from '@/lib/local-api/workout/planned-date-range';
import type { WorkoutRecord } from '~/powersync/generated/schema';
import { WorkoutUtils } from '@fytness/shared';
import { Icon } from '~/global/components/icon/Icon';
import { WorkoutItem } from '~/workout/components/workout-item/WorkoutItem';
import { useWorkoutDuplicate } from '~/workout/hooks/useWorkoutDuplicate';

interface WorkoutDayGroup {
  dateKey: string;
  label: string;
  workouts: Array<WorkoutRecord & { exerciseCount: number }>;
}

export interface WorkoutHistorySectionProps extends ViewProps {}

export const WorkoutHistorySection = ({
  className,
  ...props
}: WorkoutHistorySectionProps) => {
  const { t } = useTranslation('workout');
  const router = useRouter();
  const { dispatchDuplicateDialog } = useWorkoutDuplicate();

  // `plannedAt` is a local calendar date; compare it as one so a workout planned
  // "today"/on the week-start isn't dropped for users behind UTC (see PlannedDateRange).
  const { fromDate, toDate } = PlannedDateRange.forWeekToDate(new Date());

  const { data: workouts } = useLivePowerSyncQuery<
    WorkoutRecord & { exerciseCount: number }
  >(db.Workout.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  const getDayLabel = (date: Date): string => {
    if (isToday(date)) return t('history.today');
    if (isYesterday(date)) return t('history.yesterday');
    return format(date, 'EEEE');
  };

  const dayGroups = useMemo((): WorkoutDayGroup[] => {
    const groups = new Map<
      string,
      { date: Date; workouts: Array<WorkoutRecord & { exerciseCount: number }> }
    >();

    for (const workout of workouts) {
      if (!workout.plannedAt) continue;
      // Parse as a CALENDAR date (local midnight of its yyyy-MM-dd), not a UTC
      // instant — else a UTC-midnight plannedAt renders under the previous day
      // ("Yesterday") on devices behind UTC (see PlannedDateRange.toCalendarDate).
      const date = PlannedDateRange.toCalendarDate(workout.plannedAt);
      const key = format(date, 'yyyy-MM-dd');

      if (!groups.has(key)) {
        groups.set(key, { date, workouts: [] });
      }
      groups.get(key)!.workouts.push(workout);
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, { date, workouts: groupWorkouts }]) => ({
        dateKey,
        label: getDayLabel(date),
        workouts: groupWorkouts,
      }));
  }, [workouts, t]);

  const handleWorkoutPress = (
    workout: WorkoutRecord & { exerciseCount: number },
  ) => {
    const navigationPath = WorkoutUtils.getNavigationPath(workout);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    router.push({ pathname: navigationPath as any });
  };

  const handleAdd = () => {
    router.push({
      pathname: '/workouts/create',
      params: {
        initialDate: formatISO(new Date(), { representation: 'date' }),
      },
    });
  };

  const isEmpty = workouts.length === 0;

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center justify-between mb-3">
        <View className="flex flex-row items-center gap-3">
          <View className="h-0.5 w-6 rounded-sm bg-accent" />
          <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
            {t('history.thisWeek')}
          </Text>
        </View>
        <Pressable
          testID="workout-add-button"
          onPress={handleAdd}
          className="flex flex-row items-center gap-1.5"
        >
          <Icon name="Plus" size={14} className="text-accent" />
          <Text className="font-inter-medium text-xs text-accent">
            {t('history.add')}
          </Text>
        </Pressable>
      </View>

      {isEmpty ? (
        <View className="rounded-xl bg-surface py-8">
          <Text className="font-inter-regular text-text-muted text-center text-sm">
            {t('history.empty')}
          </Text>
        </View>
      ) : (
        <View className="flex flex-col gap-6">
          {dayGroups.map((group) => (
            <View key={group.dateKey} className="flex flex-col gap-3">
              <Text
                className="font-inter-bold text-xs text-text-secondary px-1"
                style={{ letterSpacing: 0.5 }}
              >
                {group.label}
              </Text>
              {group.workouts.map((workout) => (
                <WorkoutItem
                  key={workout.id}
                  {...workout}
                  onPress={() => handleWorkoutPress(workout)}
                  onDuplicate={() => dispatchDuplicateDialog(workout)}
                />
              ))}
            </View>
          ))}
        </View>
      )}
    </View>
  );
};
